import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import {
  CountsCell,
  EfolderDetailRow,
  EfolderName,
  EfolderRowWrap,
  MatchCell,
  MutedText,
  RowActions,
  SmallBtn,
  StageChip,
  WarningChip,
  MatterSelect,
  TextInput,
  BtnRow,
} from "../MailIntakeStyled";
import {
  SKIP_REASONS,
  describeReasonCode,
  errorMessage,
  getEfolderJob,
  preferredCandidate,
} from "../../../service/efolderUpload";

export const formatSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
  return `${(bytes / 1073741824).toFixed(2)} GB`;
};

const n = (value) => Number(value || 0).toLocaleString();

export const formatElapsed = (sinceIso, now = Date.now()) => {
  if (!sinceIso) return "";
  const ms = now - new Date(sinceIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const minutes = Math.floor(ms / 60000);
  if (minutes < 1) return "just started";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
};

// The one line the stage chip shows (spec 9.2.2). AI processing can run
// 17-35+ minutes per large zip and queues behind other matters, so a long
// "processing" is reported with its timer and never dressed up as stuck.
export const stageLabel = (row, now = Date.now()) => {
  const job = row.job;
  switch (row.stage) {
    case "uploading":
      return row.progress > 0 ? `Uploading ${row.progress}%` : "Starting upload";
    case "upload_failed":
      return "Upload failed";
    case "cancelled":
      return "Cancelled";
    case "identifying":
      return "Identifying";
    case "needs_review":
      return job?.match?.status === "auto_matched" ? "Matched, awaiting confirm" : "Needs review";
    case "queued":
      return job?.queue_position ? `Queued · #${job.queue_position} in line` : "Queued";
    case "extracting":
      return job?.total_pdfs > 0
        ? `Extracting ${n(job.extracted_count)}/${n(job.total_pdfs)}`
        : "Extracting";
    case "processing": {
      const filed = job?.counts?.categorized ?? 0;
      const total = job?.counts?.new ?? 0;
      const elapsed = formatElapsed(job?.finished_at || job?.created_at, now);
      return `AI processing: ${n(filed)} of ${n(total)} filed${elapsed ? ` · ${elapsed}` : ""}`;
    }
    case "done":
      return "Done";
    case "failed":
      return "Failed";
    case "aborted":
      return job?.match?.status === "skipped" ? "Skipped" : "Cancelled";
    default:
      return row.stage || "";
  }
};

// Which candidate the match cell describes: the same one the confirm modal
// preselects (see preferredCandidate), so Confirm files where the row says.
export const chosenCandidate = preferredCandidate;

const unmatchedLabel = (job) => {
  if (job.status === "identifying" || job.status === "pending_upload") return "Not matched yet";
  if (job.match?.status === "skipped") return "Skipped";
  return "No match";
};

const MatchSummary = ({ job }) => {
  if (!job) return <MutedText>—</MutedText>;
  const match = job.match || {};
  const candidate = chosenCandidate(job);
  const summary = candidate?.matter_summary;
  const reason = describeReasonCode(match.reason_code);
  const confidence =
    candidate?.confidence != null ? `${Math.round(candidate.confidence * 100)}%` : null;
  const others = (match.candidates || []).length - (candidate ? 1 : 0);

  if (!candidate && !job.matter_id) {
    return (
      <MatchCell>
        <strong>{unmatchedLabel(job)}</strong>
        {reason && <span>{reason}</span>}
      </MatchCell>
    );
  }
  return (
    <MatchCell>
      <strong>
        {summary?.display_name || (job.redact ? "Matter (hidden)" : `Matter ${candidate?.matter_id || job.matter_id}`)}
      </strong>
      {summary && (
        <span>
          {[summary.case_type, summary.status, summary.principal_attorney].filter(Boolean).join(" · ")}
        </span>
      )}
      {(confidence || reason) && (
        <span>{[confidence, reason].filter(Boolean).join(" · ")}</span>
      )}
      {others > 0 && job.status === "needs_review" && (
        <MutedText>+{others} other candidate{others === 1 ? "" : "s"}</MutedText>
      )}
      {match.identical_zip_job_id && (
        <span>
          <WarningChip>Identical zip already processed (#{match.identical_zip_job_id})</WarningChip>
        </span>
      )}
    </MatchCell>
  );
};

MatchSummary.propTypes = { job: PropTypes.object };

const Counts = ({ job }) => {
  const counts = job?.counts;
  if (!counts || !counts.total) return <CountsCell><MutedText>—</MutedText></CountsCell>;
  return (
    <CountsCell title="new · already on file · failed">
      {n(counts.new)} new · {n(counts.skipped_duplicate)} already on file · {n(counts.failed)} failed
    </CountsCell>
  );
};

Counts.propTypes = { job: PropTypes.object };

// Inline skip form: a reason from SKIP_REASONS and an optional note.
const SkipForm = ({ onSubmit, onCancel }) => {
  const [reason, setReason] = useState(SKIP_REASONS[0].value);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSubmit({ reason, note });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };
  return (
    <EfolderDetailRow data-testid="efolder-skip-form">
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <label style={{ minWidth: 180 }}>
          <span style={{ display: "block", marginBottom: 4 }}>Why skip this zip?</span>
          <MatterSelect
            aria-label="Skip reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          >
            {SKIP_REASONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </MatterSelect>
        </label>
        <label style={{ flex: 1, minWidth: 200 }}>
          <span style={{ display: "block", marginBottom: 4 }}>Note (optional)</span>
          <TextInput
            aria-label="Skip note"
            value={note}
            maxLength={200}
            onChange={(e) => setNote(e.target.value)}
          />
        </label>
        <BtnRow style={{ alignSelf: "flex-end" }}>
          <SmallBtn $danger type="button" onClick={submit} disabled={busy}>
            {busy ? "Skipping…" : "Skip zip"}
          </SmallBtn>
          <SmallBtn type="button" onClick={onCancel} disabled={busy}>Back</SmallBtn>
        </BtnRow>
      </div>
      {error && <div style={{ marginTop: 6 }}>{error}</div>}
    </EfolderDetailRow>
  );
};

SkipForm.propTypes = { onSubmit: PropTypes.func.isRequired, onCancel: PropTypes.func.isRequired };

const memberLabel = (member) =>
  member.member_path || member.vbms_guid || member.existing_document_id || "(name hidden)";

// What happened to each file: failed members first, then the skipped ones.
const Outcomes = ({ job }) => {
  const [state, setState] = useState({ loading: true, failed: null, skipped: null, error: null });
  useEffect(() => {
    let live = true;
    Promise.all([
      getEfolderJob(job.job_id, { members: "failed" }),
      getEfolderJob(job.job_id, { members: "skipped" }),
    ])
      .then(([failed, skipped]) => {
        if (live) setState({ loading: false, failed: failed.members, skipped: skipped.members, error: null });
      })
      .catch((err) => {
        if (live) setState({ loading: false, failed: null, skipped: null, error: errorMessage(err) });
      });
    return () => {
      live = false;
    };
  }, [job.job_id]);
  const counts = job.counts || {};
  return (
    <EfolderDetailRow data-testid="efolder-outcomes">
      <div>
        {n(counts.total)} files: {n(counts.new)} new, {n(counts.skipped_duplicate)} already on file,{" "}
        {n(counts.skipped_other)} skipped for other reasons, {n(counts.failed)} failed
        {counts.new > 0 && ` · ${n(counts.categorized)} of ${n(counts.new)} filed by AI`}
      </div>
      {state.loading && <MutedText>Loading file outcomes…</MutedText>}
      {state.error && <div>{state.error}</div>}
      {state.failed?.results?.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <strong>Failed</strong>
          <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
            {state.failed.results.map((m, i) => (
              <li key={`${memberLabel(m)}-${i}`}>{memberLabel(m)} — {m.reason}</li>
            ))}
          </ul>
          {state.failed.next_page && <MutedText>…and more</MutedText>}
        </div>
      )}
      {state.skipped?.results?.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <strong>Skipped</strong>
          <ul style={{ margin: "4px 0 0 18px", padding: 0 }}>
            {state.skipped.results.map((m, i) => (
              <li key={`${memberLabel(m)}-${i}`}>{memberLabel(m)} — {m.reason || m.outcome}</li>
            ))}
          </ul>
          {state.skipped.next_page && <MutedText>…and more</MutedText>}
        </div>
      )}
    </EfolderDetailRow>
  );
};

Outcomes.propTypes = { job: PropTypes.object.isRequired };

/**
 * One zip in the central eFolder table: name, stage chip, match, counts and
 * the actions that stage allows (spec 9.2.2-9.2.3). Review actions are
 * handed up to the page, which owns the modal and the row state.
 */
const EfolderRow = ({
  row,
  now,
  onCancel,
  onRetryUpload,
  onConfirm,
  onReassign,
  onSkip,
  onRetryIdentify,
}) => {
  const [panel, setPanel] = useState(null); // 'skip' | 'outcomes' | 'details' | null
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const job = row.job;
  const stage = row.stage;
  const reasonCode = job?.match?.reason_code;

  const run = async (fn) => {
    setBusy(true);
    setActionError(null);
    try {
      await fn();
    } catch (err) {
      setActionError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const name = row.name || (job?.redact ? `Zip #${job.job_id}` : job ? `Zip #${job.job_id}` : "Zip");
  const tone = stage === "needs_review" ? "review" : undefined;

  return (
    <div data-testid={`efolder-row-${row.key}`}>
      <EfolderRowWrap $tone={tone} role="row">
        <EfolderName>
          <strong title={name}>{name}</strong>
          <MutedText>
            {[formatSize(row.size), job?.requested_by].filter(Boolean).join(" · ")}
          </MutedText>
        </EfolderName>

        <div>
          <StageChip $stage={stage === "upload_failed" ? "failed" : stage === "cancelled" ? "aborted" : stage} data-testid="stage-chip">
            {stageLabel(row, now)}
          </StageChip>
          {row.error && <div style={{ fontSize: 12, marginTop: 4 }}>{row.error}</div>}
        </div>

        <MatchSummary job={job} />
        <Counts job={job} />

        <RowActions>
          {stage === "uploading" && (
            <SmallBtn type="button" onClick={() => onCancel(row.entry)}>Cancel</SmallBtn>
          )}
          {stage === "upload_failed" && (
            <SmallBtn type="button" onClick={() => onRetryUpload(row.entry)}>Retry upload</SmallBtn>
          )}
          {stage === "identifying" && (
            <SmallBtn type="button" onClick={() => setPanel(panel === "skip" ? null : "skip")}>Skip</SmallBtn>
          )}
          {stage === "needs_review" && (
            <>
              <SmallBtn $primary type="button" disabled={busy} onClick={() => onConfirm(job)}>Confirm</SmallBtn>
              <SmallBtn type="button" disabled={busy} onClick={() => onReassign(job)}>Change matter</SmallBtn>
              <SmallBtn type="button" disabled={busy} onClick={() => setPanel(panel === "skip" ? null : "skip")}>Skip</SmallBtn>
              {reasonCode === "identify_error" && (
                <SmallBtn type="button" disabled={busy} onClick={() => run(() => onRetryIdentify(job))}>
                  Retry identify
                </SmallBtn>
              )}
            </>
          )}
          {stage === "queued" && (
            <SmallBtn type="button" disabled={busy} onClick={() => onReassign(job)}>Change matter</SmallBtn>
          )}
          {(stage === "done" || stage === "processing") && (
            <>
              {job?.matter_id && (
                <Link to={`/matter/${job.matter_id}`}>
                  <SmallBtn type="button">Open matter</SmallBtn>
                </Link>
              )}
              <SmallBtn type="button" onClick={() => setPanel(panel === "outcomes" ? null : "outcomes")}>
                {panel === "outcomes" ? "Hide outcomes" : "View outcomes"}
              </SmallBtn>
            </>
          )}
          {stage === "failed" && (
            <>
              <SmallBtn type="button" disabled={busy} onClick={() => run(() => onRetryIdentify(job))}>
                Retry identify
              </SmallBtn>
              <SmallBtn type="button" onClick={() => setPanel(panel === "details" ? null : "details")}>
                Details
              </SmallBtn>
            </>
          )}
        </RowActions>
      </EfolderRowWrap>

      {actionError && <EfolderDetailRow>{actionError}</EfolderDetailRow>}
      {panel === "skip" && job && (
        <SkipForm
          onSubmit={async (values) => {
            await onSkip(job, values);
            setPanel(null);
          }}
          onCancel={() => setPanel(null)}
        />
      )}
      {panel === "outcomes" && job && <Outcomes job={job} />}
      {panel === "details" && job && (
        <EfolderDetailRow data-testid="efolder-details">
          {job.error_detail || "No further detail from the server."}
        </EfolderDetailRow>
      )}
    </div>
  );
};

EfolderRow.propTypes = {
  row: PropTypes.shape({
    key: PropTypes.string.isRequired,
    name: PropTypes.string,
    size: PropTypes.number,
    stage: PropTypes.string.isRequired,
    progress: PropTypes.number,
    error: PropTypes.string,
    job: PropTypes.object,
    entry: PropTypes.object,
  }).isRequired,
  now: PropTypes.number,
  onCancel: PropTypes.func.isRequired,
  onRetryUpload: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onReassign: PropTypes.func.isRequired,
  onSkip: PropTypes.func.isRequired,
  onRetryIdentify: PropTypes.func.isRequired,
};

export default EfolderRow;
